import { MdPermIdentity } from "react-icons/md";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "../../utils/api/endpoints/user/authentication";
import toast from "react-hot-toast";
import Form from "../forms/Form";
import AuthTextBox from "./inputs/AuthTextBox";

type FormData = { identity: string };

const ForgotPassword = () => {
  const [success, setSuccess] = useState(false);

  const handleSubmit = (data: FormData) => {
    requestPasswordReset(data.identity)
      .then(() => setSuccess(true))
      .catch(err => toast.error("Something went wrong"));
  };

  const defaultValues = useMemo(
    () => ({
      identity: ""
    }),
    []
  );

  return (
    <article className="mx-auto w-full max-w-[480px]">
      {/* Header brand removed per request */}
      {/**
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-3 transition-opacity hover:opacity-80">
          <img src="/ctb-logo-crop.svg" alt="CTB Logo" className="h-12 w-auto" />
          <span className="text-2xl font-semibold text-gray-900">Capture The Bug</span>
        </Link>
      </div>
      */}

      {/* Clean white card */}
      <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm md:p-10">
        {success ? (
          <>
            {/* Success State */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-400 to-green-600 opacity-10 blur-xl"></div>
                <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-lg">
                  <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="text-center">
              <h1 className="mb-4 text-2xl font-semibold text-gray-900">
                Check your email
              </h1>
              <p className="mb-6 text-gray-600">
                A link to reset your password has been sent to your email address.
                Please follow the link to set a new password.
              </p>

              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Back to Login
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Header Section */}
            <div className="mb-8">
              <h1 className="mb-2 text-2xl font-semibold text-gray-900">
                Forgot your password?
              </h1>
              <p className="text-sm text-gray-600">
                No worries! Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            <Form defaultValues={defaultValues} onSubmit={handleSubmit} className="space-y-5">
              <AuthTextBox
                label="Email Address"
                name="identity"
                placeholder="Enter your email address"
                Icon={MdPermIdentity}
                rules={{
                  required: true,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address"
                  }
                }}
              />

              {/* Submit Button - Clean professional design */}
              <button
                type="submit"
                className="w-full rounded-md bg-[#010D2C] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#010D2C] focus:ring-offset-2"
              >
                Send Reset Link
              </button>
            </Form>

            {/* Footer */}
            <div className="mt-6 border-t border-gray-100 pt-6 text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{" "}
                <Link
                  to="/login"
                  className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </article>
  );
};

export default ForgotPassword;
